/**
 * Safely parses a JSON string, tolerating null/undefined/empty values and
 * the literal strings "undefined" / "null" that can end up in localStorage
 * (e.g. from `localStorage.setItem(key, JSON.stringify(undefined))`).
 * Returns `fallback` ({} by default) instead of throwing on bad input.
 */
export function safeParseJSON<T = any>(value: string | null | undefined, fallback: T = {} as T): T {
  if (!value || value === 'undefined' || value === 'null') {
    return fallback;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

/**
 * Reads `key` from localStorage and safely JSON-parses it.
 * Never throws - returns `fallback` ({} by default) on missing/corrupt data.
 */
export function getLocalJSON<T = any>(key: string, fallback: T = {} as T): T {
  return safeParseJSON<T>(localStorage.getItem(key), fallback);
}

/**
 * True only if `key` exists in localStorage with a real, non-corrupt value.
 * Use this instead of `if (localStorage.getItem(key))` for login/auth checks,
 * since a stray literal "undefined"/"null" string is truthy and would
 * otherwise be mistaken for a real value (e.g. "logged in" when it isn't).
 */
export function isValidStored(key: string): boolean {
  const value = localStorage.getItem(key);
  return !!value && value !== 'undefined' && value !== 'null';
}

/**
 * One-time cleanup for a bug where `localStorage.setItem(key, JSON.stringify(x))`
 * was called with `x` undefined, which stores the literal string "undefined"
 * instead of removing/skipping the key. Any localStorage entry whose value is
 * exactly "undefined" or "null" is corrupt and gets removed. Safe to call on
 * every app bootstrap - it's a no-op once storage is clean.
 */
export function purgeCorruptedStorage(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) { continue; }
      const value = localStorage.getItem(key);
      if (value === 'undefined' || value === 'null') {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // localStorage unavailable (e.g. private browsing) - nothing to clean up
  }
}
